import { useState } from "react";
import { Alert, Badge, Button, Card, Grid, Group, Progress, Stack, Text, Title } from "@mantine/core";
import { IconMenu2 } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import type { KitchenToolRequest } from "@passio/api-client";
import { passio } from "../lib/clients";
import { PageHeader } from "../theme/components";
import { EmptyState, ErrorNotice, RequireFeature, ResultHeader } from "../components/common";
import { FeedbackButtons } from "../components/FeedbackButtons";
import { RouteModelPicker } from "../components/ModelPicker";
import { AiContextFields } from "../components/AiContextFields";
import { MediaInput, type MediaInputValue } from "../components/MediaInput";
import { BulletSection, KitchenPending, NutritionChips, PreferencesFields } from "../components/kitchen";
import { composeMessage } from "../lib/clientSettings";
import { buildPreferences, kitchenRequest, type PreferencesForm } from "../lib/kitchen";

const SAMPLE_MENU = `THE CORNER BISTRO
Starters: French onion soup 9 · Crispy calamari with aioli 12 · Beet and goat cheese salad 11
Mains: Steak frites (10 oz sirloin, fries, béarnaise) 28 · Grilled salmon, lentils, salsa verde 26 · Chicken parmesan with spaghetti 22 · Mushroom risotto 21 · Bistro burger with bacon, cheddar and fries 19
Sides: Fries 6 · Sautéed spinach 6 · Mac and cheese 8 · Side salad 5
Desserts: Crème brûlée 9 · Chocolate lava cake 10`;

const fitColor = (score: number) => (score >= 75 ? "lime.8" : score >= 50 ? "orange" : "red");

export function MenuCoachPage() {
  const [input, setInput] = useState<MediaInputValue>({ text: "", media: null });
  const [prefs, setPrefs] = useState<PreferencesForm>({ goals: ["high protein"] });
  const [note, setNote] = useState("");
  const [last, setLast] = useState<KitchenToolRequest | null>(null);
  const m = useMutation({ mutationFn: (req: KitchenToolRequest) => passio.tools.menuCoach(req) });

  const submit = () => {
    if (!input.text.trim() && !input.media) return;
    const req = kitchenRequest({ text: input.text, media: input.media, message: composeMessage(note), preferences: buildPreferences(prefs) });
    setLast(req);
    m.mutate(req);
  };
  const data = m.data?.data;

  return (
    <RequireFeature feature="health">
      <PageHeader
        eyebrow="POST /napi/tools/menuCoach"
        title="Menu coach"
        description="Point the camera at a menu (or paste or read it out) and get the best picks for the user's goals, diet and conditions, with how to order them and what to skip. Nutrition here is an AI estimate."
        actions={<RouteModelPicker routes={["menuCoach"]} />}
      />
      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Card>
            <Stack gap="md">
              <MediaInput value={input} onChange={setInput} textPlaceholder="Paste the menu…" photoLabel="Drop a photo of the menu" />
              {!input.media && !input.text && (
                <Button variant="subtle" color="violet" size="xs" onClick={() => setInput({ ...input, text: SAMPLE_MENU })}>
                  Use a sample bistro menu
                </Button>
              )}
              <PreferencesFields value={prefs} onChange={setPrefs} fields={["goals", "diet", "allergies", "conditions", "dislikes", "targets"]} defaultOpen />
              <AiContextFields note={note} onNoteChange={setNote} placeholder="e.g. it's a celebration, I'd like dessert" />
              <Button leftSection={<IconMenu2 size={16} />} onClick={submit} loading={m.isPending} disabled={!input.text.trim() && !input.media} size="md">
                What should I order?
              </Button>
            </Stack>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 7 }}>
          {m.error && <ErrorNotice error={m.error} onRetry={() => last && m.mutate(last)} />}
          {m.isPending && <KitchenPending title="Reading the menu…" />}
          {data && !m.isPending && (
            <Stack gap="md">
              <ResultHeader title={data.restaurant || "Your picks"} count={data.picks.length} meta={m.data?.meta} feedback={<FeedbackButtons requestId={m.data?.meta.requestId} />} />
              {data.menuItemsRead > 0 && (
                <Text fz="xs" c="dimmed" mt={-8}>
                  {data.menuItemsRead} menu items read
                </Text>
              )}
              {data.picks.length === 0 && <EmptyState title="No menu found">{data.notes || "Try a sharper photo of the menu, flat and well lit."}</EmptyState>}
              {data.picks.map((p) => (
                <Card key={p.rank + p.name}>
                  <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <Group gap="sm" wrap="nowrap" align="flex-start">
                      <Badge size="xl" circle color={p.rank === 1 ? "lime.8" : "ink.8"} variant="filled" style={{ flex: "none" }}>
                        {p.rank}
                      </Badge>
                      <div>
                        <Title order={3} fz={18}>
                          {p.name}
                        </Title>
                        {p.section && (
                          <Text fz="xs" c="dimmed">
                            {p.section}
                          </Text>
                        )}
                      </div>
                    </Group>
                    <Stack gap={2} w={110} style={{ flex: "none" }}>
                      <Text fz="xs" c="dimmed" ta="right">
                        fit {p.fitScore}/100
                      </Text>
                      <Progress value={p.fitScore} color={fitColor(p.fitScore)} size="sm" />
                    </Stack>
                  </Group>
                  <Stack gap={6} mt="sm">
                    {p.why && <Text fz="sm">{p.why}</Text>}
                    {p.tags.length > 0 && (
                      <Group gap={4}>
                        {p.tags.map((t) => (
                          <Badge key={t} size="xs" variant="light" color="violet">
                            {t}
                          </Badge>
                        ))}
                      </Group>
                    )}
                    <BulletSection title="How to order it" items={p.orderTips} />
                    <NutritionChips n={p.estimatedNutrition} size="xs" label="AI estimate" />
                  </Stack>
                </Card>
              ))}
              {data.avoid.length > 0 && (
                <Card>
                  <Text fz="xs" fw={700} c="dimmed" tt="uppercase" mb={6} style={{ letterSpacing: "0.05em" }}>
                    Skip
                  </Text>
                  <Stack gap={6}>
                    {data.avoid.map((a) => (
                      <Text key={a.name} fz="sm">
                        <b>{a.name}</b>: {a.reason}
                      </Text>
                    ))}
                  </Stack>
                </Card>
              )}
              {data.generalTips.length > 0 && (
                <Card>
                  <BulletSection title="Tips for this kind of place" items={data.generalTips} />
                </Card>
              )}
              {data.notes && (
                <Alert color="violet" variant="light">
                  <Text fz="sm">{data.notes}</Text>
                </Alert>
              )}
            </Stack>
          )}
          {!data && !m.isPending && !m.error && <EmptyState title="Eating out?">Snap the menu page by page; a short video scrolling through works for long menus.</EmptyState>}
        </Grid.Col>
      </Grid>
    </RequireFeature>
  );
}
